import { useMemo, useState } from "react"
import { useTranslation } from "react-i18next"
import { useNavigationStore } from "../stores/navigation"
import {
  Card,
  CardContent,
  CardFooter,
  CardHeader,
} from "../components/ui/card"
import { Button } from "../components/ui/button"
import { Badge } from "../components/ui/badge"
import { Input } from "../components/ui/input"
import { Separator } from "../components/ui/separator"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select"
import {
  Newspaper,
  Link as LinkIcon,
  CalendarDays,
  ExternalLink,
  Filter,
  Search,
  FileText,
  Archive,
} from "lucide-react"

// ---------------------------------------------
// Types & Sample Data
// ---------------------------------------------

type ArticleType = "press-release" | "coverage"

interface Article {
  title: string
  source: string // For press releases, this can be "EceptionistCX"
  date: string // ISO (YYYY-MM-DD)
  url: string // canonical publisher URL
  type: ArticleType // press-release | coverage
  tags?: string[]
  excerpt?: string // optional 1–2 sentence summary (esp. for press releases)
  pdfUrl?: string // optional official PDF provided by publisher
  archiveUrl?: string // optional archive snapshot (Perma/Wayback)
}

const INITIAL_ARTICLES: Article[] = [
  {
    title: "EceptionistCX expands care coordination across multi-state network",
    source: "HealthTech Today",
    date: "2025-08-12",
    url: "https://example.com/press/eceptionistcx-multistate",
    type: "coverage",
    tags: ["Care Coordination", "Enterprise"],
    excerpt:
      "Trade coverage of EceptionistCX's deployment supporting coordinated referrals across multiple states.",
  },
  {
    title:
      "Eceptionist announces CellCX partnership with CellServe to accelerate CAR T workflows",
    source: "EceptionistCX",
    date: "2025-05-19",
    url: "https://example.com/press-releases/cellcx-cellserve",
    type: "press-release",
    tags: ["CellCX", "Partnership"],
    excerpt:
      "New collaboration provides best‑practice templates and end‑to‑end workflows for cell therapy programs.",
    pdfUrl: "https://example.com/press-releases/cellcx-cellserve.pdf",
  },
  {
    title: "Provincial virtual care hub selects EceptionistCX",
    source: "Canada Digital Health",
    date: "2025-03-08",
    url: "https://example.com/press/canada-virtual-hub",
    type: "coverage",
    tags: ["Telehealth", "Canada"],
  },
  {
    title: "VaxManagerCX streamlines immunization clinics for retailers",
    source: "EceptionistCX",
    date: "2024-11-15",
    url: "https://example.com/press-releases/vaxmanager-retail",
    type: "press-release",
    tags: ["VaxManagerCX", "Retail"],
    excerpt:
      "Press release: VaxManagerCX enables smarter scheduling, inventory coordination, and compliance tracking for retail clinics.",
    archiveUrl:
      "https://web.archive.org/web/2024*/example.com/press-releases/vaxmanager-retail",
  },
]

function formatPrettyDate(iso: string) {
  const d = new Date(iso + "T00:00:00")
  return d.toLocaleDateString(undefined, {
    year: "numeric",
    month: "short",
    day: "2-digit",
  })
}

function TypeBadge({ type }: { type: ArticleType }) {
  const { t } = useTranslation()
  const label =
    type === "press-release"
      ? t("news.hero.filters.pressReleases")
      : t("news.hero.filters.inTheNews")
  const className =
    type === "press-release"
      ? "bg-[#0054A6]/10 text-[#0054A6] border border-[#0054A6]/20"
      : "bg-[#7AC043]/10 text-[#2f6f0f] border border-[#7AC043]/25"
  return (
    <span
      className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ${className}`}
    >
      {label}
    </span>
  )
}

export default function News() {
  const { t } = useTranslation()
  const { openDemoModal } = useNavigationStore()
  const [query, setQuery] = useState("")
  const [year, setYear] = useState<string | "all">("all")
  const [tag, setTag] = useState<string | "all">("all")
  const [atype, setAtype] = useState<ArticleType | "all">("all")

  const allYears = useMemo(() => {
    const years = Array.from(
      new Set(
        INITIAL_ARTICLES.map((a) =>
          new Date(a.date + "T00:00:00").getFullYear()
        )
      )
    )
      .sort((a, b) => b - a)
      .map(String)
    return years
  }, [])

  const allTags = useMemo(() => {
    const tagSet = new Set<string>()
    INITIAL_ARTICLES.forEach((a) => a.tags?.forEach((tg) => tagSet.add(tg)))
    return Array.from(tagSet).sort()
  }, [])

  const filtered = useMemo(() => {
    return INITIAL_ARTICLES.filter((a) => {
      const matchesQuery = query
        ? (
            a.title +
            " " +
            a.source +
            " " +
            (a.tags || []).join(" ") +
            " " +
            (a.excerpt || "")
          )
            .toLowerCase()
            .includes(query.toLowerCase())
        : true
      const matchesYear = year === "all" ? true : a.date.startsWith(year)
      const matchesTag = tag === "all" ? true : a.tags?.includes(tag)
      const matchesType = atype === "all" ? true : a.type === atype
      return matchesQuery && matchesYear && matchesTag && matchesType
    }).sort((a, b) => (a.date < b.date ? 1 : -1))
  }, [query, year, tag, atype])

  return (
    <div className="min-h-screen w-full">
      {/* Hero: matches About page gradient/colorway */}
      <section className="relative overflow-hidden bg-gradient-to-r from-[#0054A6] via-[#7AC043] to-[#15F0D3] text-white">
        <div className="mx-auto max-w-6xl px-6 py-16 md:py-20">
          <div className="flex items-start gap-4">
            <div className="rounded-2xl bg-white/20 p-3 shadow-sm backdrop-blur">
              <Newspaper className="h-7 w-7 text-white" />
            </div>
            <div>
              <h1 className="text-3xl font-semibold tracking-tight md:text-5xl">
                {t("news.hero.title")}
              </h1>
              <p className="mt-3 max-w-3xl text-base leading-relaxed md:text-lg text-white/90">
                {t("news.hero.subtitle")}
              </p>
            </div>
          </div>

          {/* Filters */}
          <div className="mt-8 rounded-2xl border border-white/20 bg-white/10 p-4 shadow-sm backdrop-blur">
            <div className="flex flex-col items-stretch gap-3 md:flex-row md:items-center">
              <div className="relative w-full md:flex-1">
                <Search className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-white/70" />
                <Input
                  placeholder={t("news.hero.searchPlaceholder")}
                  className="pl-9 bg-white text-black placeholder:text-gray-500"
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  aria-label="Search articles"
                />
              </div>

              <div className="flex flex-wrap items-center gap-2">
                <Filter className="h-4 w-4 text-white/70" />

                {/* Type */}
                <Select onValueChange={(v) => setAtype(v as any)} value={atype}>
                  <SelectTrigger className="w-[170px] bg-white text-black">
                    <SelectValue placeholder="Type" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">
                      {t("news.hero.filters.allTypes")}
                    </SelectItem>
                    <SelectItem value="press-release">
                      {t("news.hero.filters.pressReleases")}
                    </SelectItem>
                    <SelectItem value="coverage">
                      {t("news.hero.filters.inTheNews")}
                    </SelectItem>
                  </SelectContent>
                </Select>

                {/* Year */}
                <Select onValueChange={(v) => setYear(v as any)} value={year}>
                  <SelectTrigger className="w-[130px] bg-white text-black">
                    <SelectValue placeholder="Year" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">
                      {t("news.hero.filters.allYears")}
                    </SelectItem>
                    {allYears.map((y) => (
                      <SelectItem key={y} value={y}>
                        {y}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>

                {/* Tag */}
                <Select onValueChange={(v) => setTag(v as any)} value={tag}>
                  <SelectTrigger className="w-[160px] bg-white text-black">
                    <SelectValue placeholder="Tag" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">
                      {t("news.hero.filters.allTags")}
                    </SelectItem>
                    {allTags.map((tag) => (
                      <SelectItem key={tag} value={tag}>
                        {tag}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* List */}
      <section className="mx-auto max-w-6xl px-6 py-12 pb-20">
        <div className="grid gap-5 md:grid-cols-2 lg:grid-cols-3">
          {filtered.map((a) => (
            <Card
              key={a.url}
              className="group flex flex-col rounded-2xl border shadow-sm transition hover:shadow-md"
            >
              <CardHeader className="space-y-2">
                <div className="flex items-center justify-between gap-3">
                  <div className="flex items-center gap-2">
                    <span className="inline-flex h-8 w-8 items-center justify-center rounded-full border bg-white text-xs font-semibold">
                      {a.source
                        .split(" ")
                        .map((w) => w[0])
                        .join("")
                        .slice(0, 3)
                        .toUpperCase()}
                    </span>
                    <span className="text-sm text-muted-foreground">
                      {a.source}
                    </span>
                  </div>
                  <div className="flex items-center gap-2">
                    <TypeBadge type={a.type} />
                    <span className="inline-flex items-center gap-1 text-xs text-muted-foreground">
                      <CalendarDays className="h-3.5 w-3.5" />
                      {formatPrettyDate(a.date)}
                    </span>
                  </div>
                </div>
                <h3 className="text-lg font-semibold leading-snug md:text-xl">
                  {a.title}
                </h3>
              </CardHeader>
              <CardContent className="pt-0 flex-1">
                {a.excerpt && (
                  <p className="text-sm text-muted-foreground">{a.excerpt}</p>
                )}
                {a.tags && a.tags.length > 0 && (
                  <div className="mt-3 flex flex-wrap gap-1.5">
                    {a.tags.map((t) => (
                      <Badge
                        key={t}
                        variant="secondary"
                        className="rounded-full"
                      >
                        {t}
                      </Badge>
                    ))}
                  </div>
                )}
              </CardContent>
              <CardFooter className="flex flex-wrap gap-2 pt-0 mt-auto">
                <Button
                  asChild
                  variant="outline"
                  className="group/button rounded-xl"
                >
                  <a
                    href={a.url}
                    target="_blank"
                    rel="noreferrer noopener"
                    aria-label={`Open article: ${a.title}`}
                  >
                    <LinkIcon className="mr-2 h-4 w-4 transition group-hover/button:translate-x-0.5" />
                    {t("news.articles.readOnPublisherSite")}
                    <ExternalLink className="ml-2 h-4 w-4 opacity-70" />
                  </a>
                </Button>
                {a.pdfUrl && (
                  <Button asChild variant="secondary" className="rounded-xl">
                    <a
                      href={a.pdfUrl}
                      target="_blank"
                      rel="noreferrer noopener"
                      aria-label={`Open PDF: ${a.title}`}
                    >
                      <FileText className="mr-2 h-4 w-4" />{" "}
                      {t("news.articles.downloadPDF")}
                    </a>
                  </Button>
                )}
                {a.archiveUrl && (
                  <Button asChild variant="ghost" className="rounded-xl">
                    <a
                      href={a.archiveUrl}
                      target="_blank"
                      rel="noreferrer noopener"
                      aria-label={`Open archive snapshot: ${a.title}`}
                    >
                      <Archive className="mr-2 h-4 w-4" />{" "}
                      {t("news.articles.viewArchivedCopy")}
                    </a>
                  </Button>
                )}
              </CardFooter>
            </Card>
          ))}
        </div>

        {filtered.length === 0 && (
          <div className="mt-16 rounded-2xl border bg-white p-10 text-center shadow-sm">
            <p className="text-lg font-medium">
              {t("news.articles.noArticlesFound")}
            </p>
            <p className="mt-1 text-muted-foreground">
              {t("news.articles.tryClearingFilters")}
            </p>
          </div>
        )}

        <Separator className="my-12" />

        {/* CTA Section */}
        <div className="mx-auto max-w-4xl rounded-2xl border bg-gradient-to-r from-[#0054A6] via-[#7AC043] to-[#15F0D3] p-8 text-center text-white">
          <h2 className="text-2xl font-bold mb-4">{t("news.cta.title")}</h2>
          <p className="mb-6 text-white/90">{t("news.cta.subtitle")}</p>
          <button
            onClick={openDemoModal}
            className="inline-flex items-center justify-center rounded-xl bg-white px-6 py-3 text-sm font-semibold text-slate-900 shadow hover:shadow-md transition"
          >
            {t("common.bookDemo")}
          </button>
        </div>

        {/* Press / Media contact */}
        <div className="mx-auto max-w-4xl rounded-2xl border bg-muted/30 p-6">
          <h2 className="text-xl font-semibold">
            {t("news.pressContact.title")}
          </h2>
          <p className="mt-2 text-sm text-muted-foreground">
            {t("news.pressContact.description")}{" "}
            <a
              className="font-medium text-foreground underline"
              href={`mailto:${t("news.pressContact.email")}`}
            >
              {t("news.pressContact.email")}
            </a>
            .
          </p>
        </div>

        {/* <div className="mt-8 rounded-2xl border bg-muted/30 p-6 text-sm leading-relaxed text-muted-foreground">
          <p className="font-medium text-foreground">
            {t("news.maintenance.title")}
          </p>
          <ul className="mt-2 list-disc pl-5">
            <li>Use ISO dates (YYYY-MM-DD) for correct sorting.</li>
            <li>
              Set type to "press-release" for company-authored items and
              "coverage" for third‑party articles.
            </li>
            <li>
              Optionally include excerpt (1–2 sentences) for context, especially
              on press releases.
            </li>
            <li>
              If available, add pdfUrl and/or archiveUrl; the buttons render
              automatically.
            </li>
            <li>
              Tags power filtering and quick scanning (e.g., AI, Care
              Coordination, CellCX).
            </li>
          </ul>
        </div> */}
      </section>
    </div>
  )
}
